// Slate 0.59 on Windows Chrome fires insertText twice per IME commit (once via
// beforeinput, once via compositionend). This plugin drops the duplicate, but
// only during an active composition or within 50ms of compositionend — normal
// typing and OS key-repeat are untouched since isComposing/lastCompositionEnd
// stay at their defaults.

export type ImeDedupRefs = {
  isEnabled: { current: boolean };
  isComposing: { current: boolean };
  lastCompositionEnd: { current: number };
};

export type ImeDedupEditor<T> = T & { resetImeDedup: () => void };

export function withImeDedup<T extends { insertText: (text: string) => void }>(
  editor: T,
  refs: ImeDedupRefs
): ImeDedupEditor<T> {
  const { insertText } = editor;
  let lastText: string | null = null;
  let lastTime = 0;

  // Reset on compositionstart so a new session doesn't inherit stale
  // lastText/lastTime and incorrectly dedup its first character.
  const dedupEditor: ImeDedupEditor<T> = Object.assign(editor, {
    resetImeDedup: () => {
      lastText = null;
      lastTime = 0;
    },
  });

  dedupEditor.insertText = (text: string) => {
    if (!refs.isEnabled.current) {
      insertText(text);

      return;
    }

    const now = Date.now();
    const isWithinCompositionWindow =
      refs.isComposing.current || now - refs.lastCompositionEnd.current < 50;

    if (
      isWithinCompositionWindow &&
      text &&
      text === lastText &&
      now - lastTime < 50
    ) {
      lastText = null;

      return;
    }

    lastText = text;
    lastTime = now;
    insertText(text);
  };

  return dedupEditor;
}
